import { EditableTableCell } from 'components/EditableTableCell';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { DeviationEnum } from 'gql/graphql';
import { replacerCost } from 'utils/replacerCost';

import type { CSSProperties } from 'react';
import { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
    updateActKC2OtherCostAcceptedCost,
    updateActKC2OtherCostPlanCost,
} from '../../WorkOrderOthersTable/workOrderOtherTableQueries';

import Styles from './cellRenderer.module.css';

interface IWorkOrderOtherCostEdit {
    actKCId: number;
    workOrderOtherId: number;
    defaultValue: string;
    decimal: number;
    isDisabled: boolean;
    reloadDataHandler: (reloadRowsIds?: number[] | null) => void;
    valueType: 'plan' | 'accepted';
    style?: CSSProperties;
    deviation?: DeviationEnum;
}

export const WorkOrderOtherKC2CostEdit = memo((params: IWorkOrderOtherCostEdit) => {
    const dispatch = useDispatch();

    const {
        actKCId,
        defaultValue,
        decimal = 2,
        isDisabled,
        reloadDataHandler,
        valueType,
        style,
        workOrderOtherId,
        deviation,
    } = params;

    const updateActPlanCostId = useMemo(() => `updateActPlanCost${workOrderOtherId}`, [workOrderOtherId]);
    const updateActAcceptedCost = useMemo(() => `updateActAcceptedCost${workOrderOtherId}`, [workOrderOtherId]);

    const [updateActPlanCostReset] = useResetStoreData({
        requestId: updateActPlanCostId,
    });

    const [updateActAcceptedCostReset] = useResetStoreData({
        requestId: updateActAcceptedCost,
    });

    const onSuccess = useCallback(() => {
        if (valueType === 'plan') {
            updateActPlanCostReset(undefined);
        } else {
            updateActAcceptedCostReset(undefined);
        }
    }, [updateActAcceptedCostReset, updateActPlanCostReset, valueType]);

    const [
        updateActPlanCostData,
        updateActPlanCostStatus,
        updateActPlanCostErr,
        updateActPlanCostRequest,
        ,
        updateActPlanCostAbortRequest,
    ] = useRequest({
        requestId: updateActPlanCostId,
        query: updateActKC2OtherCostPlanCost,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const [
        updateActAcceptedCostData,
        updateActAcceptedCostStatus,
        updateActAcceptedCostErr,
        updateActAcceptedCostRequest,
        ,
        updateActAcceptedCostAbortRequest,
    ] = useRequest({
        requestId: updateActAcceptedCost,
        query: updateActKC2OtherCostAcceptedCost,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const onChangeHandler = useCallback(
        (newInputValue: string | null) => {
            if (valueType === 'plan') {
                if (updateActPlanCostStatus === RequestStatus.FETCHING) {
                    dispatch(updateActPlanCostAbortRequest());
                }

                dispatch(
                    updateActPlanCostRequest({
                        actKC2Id: actKCId,
                        workOrderOtherId,
                        cost: replacerCost(newInputValue ?? 0),
                    }),
                );
            } else {
                if (updateActAcceptedCostStatus === RequestStatus.FETCHING) {
                    dispatch(updateActAcceptedCostAbortRequest());
                }

                dispatch(
                    updateActAcceptedCostRequest({
                        actKC2Id: actKCId,
                        workOrderOtherId,
                        cost: replacerCost(newInputValue ?? 0),
                    }),
                );
            }
        },
        [
            actKCId,
            workOrderOtherId,
            dispatch,
            updateActAcceptedCostAbortRequest,
            updateActAcceptedCostRequest,
            updateActAcceptedCostStatus,
            updateActPlanCostAbortRequest,
            updateActPlanCostRequest,
            updateActPlanCostStatus,
            valueType,
        ],
    );

    return (
        <EditableTableCell
            className={Styles.otherCostEdit}
            rowId={workOrderOtherId}
            decimalScale={decimal}
            disabled={isDisabled}
            fieldValue={defaultValue}
            reloadHandler={reloadDataHandler}
            updateRequest={onChangeHandler}
            leftSide={deviation === DeviationEnum.Up ? '▲' : undefined}
            updateStatus={valueType === 'plan' ? updateActPlanCostStatus : updateActAcceptedCostStatus}
            requestResultObj={
                valueType === 'plan'
                    ? updateActPlanCostData?.uerSetActKC2OtherCostPlanCost
                    : updateActAcceptedCostData?.uerSetActKC2OtherCostAcceptedCost
            }
            requestErrors={valueType === 'plan' ? updateActPlanCostErr : updateActAcceptedCostErr}
            errorModalTitle="Редактирование Стоимости"
            inputStyles={style}
        />
    );
});

WorkOrderOtherKC2CostEdit.displayName = 'WorkOrderOtherKC2CostEdit';
